import { useMemo, useRef, useState } from 'react';

import type { ReviewFeedbackKind } from '../../services/reviewService';

const TRACK_HEIGHT = 240;

const MEMORY_BRANCHES = [
  {
    score: 92,
    rating: 'proficient' as const,
    label: '熟悉',
    description: '已经记牢，可以较快迁移',
  },
  {
    score: 70,
    rating: 'fuzzy' as const,
    label: '模糊',
    description: '有印象但不稳，需要再巩固',
  },
  {
    score: 35,
    rating: 'forgotten' as const,
    label: '忘记',
    description: '基本想不起来，需要重新学习',
  },
] as const;

function mapScoreToRating(score: number): ReviewFeedbackKind {
  if (score >= 85) return 'proficient';
  if (score >= 60) return 'fuzzy';
  return 'forgotten';
}

function mapRatingToScore(rating: ReviewFeedbackKind | null | undefined): number | null {
  if (rating === 'proficient') return 92;
  if (rating === 'fuzzy') return 70;
  if (rating === 'forgotten') return 35;
  return null;
}

function scoreToOffset(score: number, trackHeight: number) {
  return (1 - score / 100) * trackHeight;
}

function offsetToScore(offsetY: number, trackHeight: number) {
  const ratio = Math.max(0, Math.min(1, offsetY / trackHeight));
  return Math.round((1 - ratio) * 100);
}

type MemoryScoreTrackProps = {
  currentRating?: ReviewFeedbackKind | null;
  disabled?: boolean;
  onSelect: (rating: ReviewFeedbackKind) => void;
};

/** 刻意练习：连续过渡色评分色带（提交仍映射熟悉/模糊/忘记）。 */
export function MemoryScoreTrack({
  currentRating = null,
  disabled = false,
  onSelect,
}: MemoryScoreTrackProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [draftScore, setDraftScore] = useState<number | null>(null);
  const committedScore = mapRatingToScore(currentRating);
  const displayScore = draftScore ?? committedScore;
  const hasScore = typeof displayScore === 'number' && Number.isFinite(displayScore);
  const thumbTop = hasScore ? scoreToOffset(displayScore, TRACK_HEIGHT) : null;
  const active = useMemo(() => {
    if (!hasScore) return null;
    const rating = mapScoreToRating(displayScore);
    return MEMORY_BRANCHES.find((branch) => branch.rating === rating) ?? null;
  }, [displayScore, hasScore]);

  function commitScore(score: number) {
    if (disabled) return;
    setDraftScore(score);
    onSelect(mapScoreToRating(score));
  }

  function applyFromClientY(clientY: number) {
    if (disabled || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    commitScore(offsetToScore(clientY - rect.top, TRACK_HEIGHT));
  }

  return (
    <div className="mt-6 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">拖动色带打分</h3>
        <span className="text-xs text-muted">
          {hasScore ? `${displayScore} 分` : '未选择'}
        </span>
      </div>

      <div className="flex items-stretch gap-4" style={{ minHeight: TRACK_HEIGHT }}>
        <div
          ref={trackRef}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={hasScore ? displayScore : undefined}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className={`relative w-7 shrink-0 overflow-hidden rounded-full border border-black/10 ${
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
          style={{
            height: TRACK_HEIGHT,
            background: 'linear-gradient(to bottom, #16A34A, #65A30D, #CA8A04, #EA580C, #DC2626)',
          }}
          onPointerDown={(event) => {
            if (disabled) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            applyFromClientY(event.clientY);
          }}
          onPointerMove={(event) => {
            if (disabled || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
            applyFromClientY(event.clientY);
          }}
        >
          {MEMORY_BRANCHES.map((branch) => (
            <span
              key={`memory-marker-${branch.rating}`}
              className={`pointer-events-none absolute left-1.5 h-0.5 w-4 rounded-full ${
                branch.rating === currentRating || (hasScore && mapScoreToRating(displayScore) === branch.rating)
                  ? 'bg-white'
                  : 'bg-white/70'
              }`}
              style={{ top: scoreToOffset(branch.score, TRACK_HEIGHT) - 1 }}
            />
          ))}
          {thumbTop != null ? (
            <span
              className="pointer-events-none absolute left-1 h-5 w-5 -translate-y-1/2 rounded-full border-2 border-slate-900 bg-white shadow"
              style={{ top: thumbTop }}
            />
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col justify-between gap-2">
          {MEMORY_BRANCHES.map((branch) => {
            const selected = branch.rating === currentRating
              || (hasScore && mapScoreToRating(displayScore) === branch.rating);
            return (
              <button
                key={branch.rating}
                type="button"
                disabled={disabled}
                onClick={() => commitScore(branch.score)}
                className={`rounded-xl border px-3 py-2.5 text-left text-sm transition-colors disabled:opacity-60 ${
                  selected
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                    : 'border-border bg-surface text-ink hover:bg-black/[0.02]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{branch.label}</span>
                  <span className="text-xs text-muted">{branch.score}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted">{branch.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {active ? (
        <p className="text-xs text-muted">
          {active.label} · {active.description}
        </p>
      ) : null}
    </div>
  );
}
